import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';

import { fail, ok } from '../models/TranscriptResult';

import type { TranscriptResult } from '../models/TranscriptResult';
import type { ClipTranscriptToken } from '@src/editor/types';
import type { TranscriptElementLegacy } from '@src/types.ts/transcript';

// Phase 1: Pure punctuation helpers (no in-place mutation of caller segment)

export interface PunctuationChangeSummary {
  removedCount: number;
  restoredCount: number;
  fromIndex: number;
  toIndex: number;
}

/**
 * Remove punctuation tokens (type === 'punct') in [fromIndex, toIndex] producing a new segment.
 * Text tokens are preserved. Non-target punctuation untouched. Returns TranscriptResult.
 */
export function removePunctuationRange(
  segment: ClipTranscriptSegment,
  fromIndex: number,
  toIndex: number,
): TranscriptResult<{
  segment: ClipTranscriptSegment;
  summary: PunctuationChangeSummary;
}> {
  if (fromIndex > toIndex) return fail('invalid_range', { fromIndex, toIndex });
  const original = segment.getWordsWithPunctuation();
  const upperBound = original.length - 1;
  if (fromIndex < 0 || toIndex > upperBound) {
    return fail('index_out_of_bounds', {
      fromIndex,
      toIndex,
      upperBound,
      segmentId: segment.id,
    });
  }

  let changed = false;
  const nextTokens: ClipTranscriptToken[] = original.map((t, idx) => {
    if (idx < fromIndex || idx > toIndex) return t; // outside range
    if (t.type === 'punct' && t.state !== 'removed') {
      changed = true;
      return { ...t, state: 'removed' };
    }
    return t;
  });

  if (!changed) {
    return ok({
      segment,
      summary: { removedCount: 0, restoredCount: 0, fromIndex, toIndex },
    });
  }

  const removedCount = nextTokens.filter(
    (t, idx) =>
      idx >= fromIndex &&
      idx <= toIndex &&
      t.type === 'punct' &&
      t.state === 'removed',
  ).length;

  const newSegment = new ClipTranscriptSegment(
    nextTokens.filter(t => t.state !== 'removed'),
    segment.mediaStart,
    segment.mediaEnd,
  );
  return ok({
    segment: newSegment,
    summary: { removedCount, restoredCount: 0, fromIndex, toIndex },
  });
}

/**
 * Restore previously removed punctuation tokens (state === 'removed') in [fromIndex, toIndex].
 */
export function restorePunctuationRange(
  segment: ClipTranscriptSegment,
  fromIndex: number,
  toIndex: number,
): TranscriptResult<{
  segment: ClipTranscriptSegment;
  summary: PunctuationChangeSummary;
}> {
  if (fromIndex > toIndex) return fail('invalid_range', { fromIndex, toIndex });
  const original = segment.getWordsWithPunctuation();
  const upperBound = original.length - 1;
  if (fromIndex < 0 || toIndex > upperBound) {
    return fail('index_out_of_bounds', {
      fromIndex,
      toIndex,
      upperBound,
      segmentId: segment.id,
    });
  }
  let restoredCount = 0;
  const nextTokens: ClipTranscriptToken[] = original.map((t, idx) => {
    if (idx < fromIndex || idx > toIndex) return t;
    if (t.type === 'punct' && t.state === 'removed') {
      restoredCount += 1;
      const cloned: ClipTranscriptToken = { ...t } as ClipTranscriptToken;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (cloned as any).state;
      return cloned;
    }
    return t;
  });

  if (restoredCount === 0) {
    return ok({
      segment,
      summary: { removedCount: 0, restoredCount: 0, fromIndex, toIndex },
    });
  }

  const newSegment = new ClipTranscriptSegment(
    nextTokens,
    segment.mediaStart,
    segment.mediaEnd,
  );
  return ok({
    segment: newSegment,
    summary: { removedCount: 0, restoredCount, fromIndex, toIndex },
  });
}

export function trimTextAndFilterOutWhitespaces<
  T extends TranscriptElementLegacy,
>(tokens: readonly T[]): T[] {
  return tokens
    .filter(
      t =>
        t.type === 'text' ||
        (t.type === 'punct' && t.value && /[^\t ]/.test(t.value)),
    ) // new-lines allowed in punct
    .map(t => {
      if (t.type === 'text' && t.value) {
        t.value = t.value.trim();
      }
      return t;
    });
}

/**
 * Punctuation that typically doesn't require space before (English rules)
 */
const NO_SPACE_BEFORE = new Set([
  '.',
  ',',
  '!',
  '?',
  ';',
  ':',
  ')',
  ']',
  '}',
  '...',
  '—',
  '–',
  "'",
  '"',
]);

/**
 * Punctuation that typically doesn't require space after (English rules)
 */
const NO_SPACE_AFTER = new Set(['(', '[', '{', "'", '"', '\n']);

/**
 * Determines if whitespace should be added before a token
 */
function needsSpaceBefore(
  token: TranscriptElementLegacy,
  prevToken?: TranscriptElementLegacy,
): boolean {
  if (!prevToken || !token.value) return false;

  // No space before punctuation that's in the NO_SPACE_BEFORE set
  if (token.type === 'punct' && NO_SPACE_BEFORE.has(token.value)) {
    return false;
  }

  // Add space between text tokens
  if (prevToken.type === 'text' && token.type === 'text') {
    return true;
  }

  // Add space after punctuation (unless it's in NO_SPACE_AFTER)
  if (prevToken.type === 'punct' && prevToken.value) {
    return !NO_SPACE_AFTER.has(prevToken.value);
  }

  return false;
}

/**
 * Fill in whitespaces in transcript elements according to English language rules
 *
 * This function inserts space punct elements between tokens where appropriate,
 * following standard English typography rules:
 * - Space between words
 * - No space before: . , ! ? ; : ) ] } ' " etc.
 * - No space after: ( [ { ' " etc.
 * - Space after sentence-ending punctuation
 *
 * @param tokens - Array of transcript elements (text and punctuation)
 * @returns Array of transcript elements with space punct elements inserted
 *
 * @example
 * fillInWhitespacesInTranscriptElements([
 *   { type: 'text', value: 'Hello' },
 *   { type: 'punct', value: ',' },
 *   { type: 'text', value: 'world' },
 *   { type: 'punct', value: '!' },
 * ])
 * // Returns:
 * // [
 * //   { type: 'text', value: 'Hello' },
 * //   { type: 'punct', value: ',' },
 * //   { type: 'punct', value: ' ' },
 * //   { type: 'text', value: 'world' },
 * //   { type: 'punct', value: '!' },
 * // ]
 *
 * @example
 * fillInWhitespacesInTranscriptElements([
 *   { type: 'punct', value: '(' },
 *   { type: 'text', value: 'Note' },
 *   { type: 'punct', value: ')' },
 * ])
 * // Returns:
 * // [
 * //   { type: 'punct', value: '(' },
 * //   { type: 'text', value: 'Note' },
 * //   { type: 'punct', value: ')' },
 * // ]
 */
export function fillInWhitespacesInTranscriptElements<
  T extends TranscriptElementLegacy,
>(tokens: T[]): T[] {
  if (tokens.length === 0) return [];

  const result: T[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const prevToken = i > 0 ? tokens[i - 1] : undefined;

    // Insert space before current token if needed
    if (needsSpaceBefore(token, prevToken)) {
      const spaceToken = {
        type: 'punct',
        subtype: 'whitespace',
        state: token.state,
        value: ' ',
        initial_index: -1,
      } as T;
      result.push(spaceToken);
    }

    result.push(token);
  }

  return result;
}

export function addSpaceToEndOfTranscriptElements<
  T extends TranscriptElementLegacy,
>(tokens: T[]): T[] {
  if (tokens.length === 0) return [];

  const lastToken = tokens[tokens.length - 1];
  if (lastToken.subtype === 'whitespace' || lastToken.value === ' ') {
    return tokens; // already has space at the end
  }

  return [
    ...tokens,
    {
      type: 'punct',
      subtype: 'whitespace',
      value: ' ',
      initial_index: -1,
    } as T,
  ];
}
